import api from './api';
import { LoginCredentials, RegisterData, AuthResponse, User, DashboardPreferences } from '../types';

export interface UserProfileUpdate {
  full_name?: string;
  language?: string;
  temperature_unit?: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/api/auth/login', credentials, {
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }

    return response.data;
  },

  async register(data: RegisterData): Promise<User> {
    const response = await api.post<User>('/api/auth/register', data);
    return response.data;
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/api/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/api/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/api/auth/me');
    return response.data;
  },

  async updateProfile(data: UserProfileUpdate): Promise<User> {
    const response = await api.put<User>('/api/auth/me', data);
    return response.data;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  async heartbeat(): Promise<void> {
    await api.post('/api/auth/session/heartbeat');
  },

  logout(): void {
    const token = localStorage.getItem('token');
    localStorage.removeItem('token');
    if (token) {
      void api.post('/api/auth/logout', undefined, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {
        // Local logout has already succeeded if the session expired or the network is unavailable.
      });
    }
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  },

  async getDashboardPreferences(): Promise<DashboardPreferences> {
    const response = await api.get<DashboardPreferences>('/api/auth/preferences');
    return response.data;
  },

  async saveDashboardPreferences(prefs: DashboardPreferences): Promise<DashboardPreferences> {
    const response = await api.put<DashboardPreferences>('/api/auth/preferences', prefs);
    return response.data;
  },
};
